import { Effect, ShaderStore } from '@babylonjs/core';

/** How far (screen pixels) the rings can reach out from a silhouette in all: the loop's bound, so widths past it are cut short. */
export const MESH_OUTLINE_MAX_REACH = 6;
/** How many colors each ring can draw in at once: the mask stores a color slot (1 to this) per ring. */
export const MESH_OUTLINE_COLOR_SLOTS = 4;

/**
 * Registers the post-process `MeshOutlines` draws its rings with:
 *
 * - `meshOutline` - reads the outline mask (red: a body's effect ring
 *   color slot, green: its target ring slot, each slot / 4; a whole
 *   character is one silhouette, so its eyes and gear seams never get a
 *   line) and, for every pixel outside all silhouettes, how far the nearest
 *   red and green pixels are and their slots. Within `effectWidth` of red it
 *   draws the effect ring; the target ring sits within `targetWidth` past
 *   that (just outside the effect ring where both outline a body, right at
 *   the silhouette where only the target does). Each ring is its slot's
 *   color, mixed over the scene by that color's alpha.
 *
 * The mask is sampled at level 0 inside the loop: WGSL only allows the
 * implicit-derivative `textureSample` in uniform control flow. Registered
 * into both the GLSL and the WGSL store for the same reason as
 * `essence-stream-shaders.ts`; the WGSL port is kept line-for-line
 * equivalent. Idempotent.
 */
export function registerMeshOutlineShaders(): void {
    Effect.ShadersStore['meshOutlineFragmentShader'] = `
        precision highp float;
        varying vec2 vUV;
        uniform sampler2D textureSampler;
        uniform sampler2D maskSampler;
        uniform vec2 texelSize;
        uniform vec4 effectColor1;
        uniform vec4 effectColor2;
        uniform vec4 effectColor3;
        uniform vec4 effectColor4;
        uniform vec4 targetColor1;
        uniform vec4 targetColor2;
        uniform vec4 targetColor3;
        uniform vec4 targetColor4;
        uniform float effectWidth;
        uniform float targetWidth;

        float slotOf(float value) {
            return floor(value * ${MESH_OUTLINE_COLOR_SLOTS}.0 + 0.5);
        }

        vec4 pick(float slot, vec4 one, vec4 two, vec4 three, vec4 four) {
            return slot < 1.5 ? one : slot < 2.5 ? two : slot < 3.5 ? three : four;
        }

        void main(void) {
            vec4 scene = texture2D(textureSampler, vUV);
            vec2 here = texture2D(maskSampler, vUV).rg;
            float reach = effectWidth + targetWidth + 0.5;
            float nearEffect = 1000.0;
            float nearTarget = 1000.0;
            float effectSlot = 0.0;
            float targetSlot = 0.0;
            for (int y = -${MESH_OUTLINE_MAX_REACH}; y <= ${MESH_OUTLINE_MAX_REACH}; y++) {
                for (int x = -${MESH_OUTLINE_MAX_REACH}; x <= ${MESH_OUTLINE_MAX_REACH}; x++) {
                    float d = length(vec2(float(x), float(y)));
                    if (d > reach) {
                        continue;
                    }
                    vec2 mask = texture2D(maskSampler, vUV + vec2(float(x), float(y)) * texelSize).rg;
                    float red = slotOf(mask.r);
                    float green = slotOf(mask.g);
                    if (red > 0.5 && d < nearEffect) {
                        nearEffect = d;
                        effectSlot = red;
                    }
                    if (green > 0.5 && d < nearTarget) {
                        nearTarget = d;
                        targetSlot = green;
                    }
                }
            }

            vec3 color = scene.rgb;
            if (slotOf(here.r) < 0.5 && slotOf(here.g) < 0.5) {
                float targetFrom = nearEffect < reach ? effectWidth : 0.0;
                if (nearEffect <= effectWidth + 0.5) {
                    vec4 ring = pick(effectSlot, effectColor1, effectColor2, effectColor3, effectColor4);
                    color = mix(color, ring.rgb, ring.a);
                } else if (nearTarget <= targetFrom + targetWidth + 0.5) {
                    vec4 ring = pick(targetSlot, targetColor1, targetColor2, targetColor3, targetColor4);
                    color = mix(color, ring.rgb, ring.a);
                }
            }
            gl_FragColor = vec4(color, scene.a);
        }
    `;

    ShaderStore.ShadersStoreWGSL['meshOutlineFragmentShader'] = `
        varying vUV: vec2f;
        var textureSamplerSampler: sampler;
        var textureSampler: texture_2d<f32>;
        var maskSamplerSampler: sampler;
        var maskSampler: texture_2d<f32>;
        uniform texelSize: vec2f;
        uniform effectColor1: vec4f;
        uniform effectColor2: vec4f;
        uniform effectColor3: vec4f;
        uniform effectColor4: vec4f;
        uniform targetColor1: vec4f;
        uniform targetColor2: vec4f;
        uniform targetColor3: vec4f;
        uniform targetColor4: vec4f;
        uniform effectWidth: f32;
        uniform targetWidth: f32;

        fn slotOf(value: f32) -> f32 {
            return floor(value * ${MESH_OUTLINE_COLOR_SLOTS}.0 + 0.5);
        }

        fn pick(slot: f32, one: vec4f, two: vec4f, three: vec4f, four: vec4f) -> vec4f {
            return select(select(select(four, three, slot < 3.5), two, slot < 2.5), one, slot < 1.5);
        }

        @fragment
        fn main(input: FragmentInputs) -> FragmentOutputs {
            let scene = textureSample(textureSampler, textureSamplerSampler, input.vUV);
            let here = textureSampleLevel(maskSampler, maskSamplerSampler, input.vUV, 0.0).rg;
            let reach = uniforms.effectWidth + uniforms.targetWidth + 0.5;
            var nearEffect: f32 = 1000.0;
            var nearTarget: f32 = 1000.0;
            var effectSlot: f32 = 0.0;
            var targetSlot: f32 = 0.0;
            for (var y: i32 = -${MESH_OUTLINE_MAX_REACH}; y <= ${MESH_OUTLINE_MAX_REACH}; y++) {
                for (var x: i32 = -${MESH_OUTLINE_MAX_REACH}; x <= ${MESH_OUTLINE_MAX_REACH}; x++) {
                    let d = length(vec2f(f32(x), f32(y)));
                    if (d > reach) {
                        continue;
                    }
                    let mask = textureSampleLevel(maskSampler, maskSamplerSampler, input.vUV + vec2f(f32(x), f32(y)) * uniforms.texelSize, 0.0).rg;
                    let red = slotOf(mask.r);
                    let green = slotOf(mask.g);
                    if (red > 0.5 && d < nearEffect) {
                        nearEffect = d;
                        effectSlot = red;
                    }
                    if (green > 0.5 && d < nearTarget) {
                        nearTarget = d;
                        targetSlot = green;
                    }
                }
            }

            var color = scene.rgb;
            if (slotOf(here.r) < 0.5 && slotOf(here.g) < 0.5) {
                let targetFrom = select(0.0, uniforms.effectWidth, nearEffect < reach);
                if (nearEffect <= uniforms.effectWidth + 0.5) {
                    let ring = pick(effectSlot, uniforms.effectColor1, uniforms.effectColor2, uniforms.effectColor3, uniforms.effectColor4);
                    color = mix(color, ring.rgb, ring.a);
                } else if (nearTarget <= targetFrom + uniforms.targetWidth + 0.5) {
                    let ring = pick(targetSlot, uniforms.targetColor1, uniforms.targetColor2, uniforms.targetColor3, uniforms.targetColor4);
                    color = mix(color, ring.rgb, ring.a);
                }
            }
            fragmentOutputs.color = vec4f(color, scene.a);
        }
    `;
}
